import React, { useState, useEffect } from 'react';
import API from '@/lib/api';
import { getErrorMessage } from '@/lib/errorHandler';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Save, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [savedSettings, setSavedSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [reloadingRateLimits, setReloadingRateLimits] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('general');

  const categories = [
    { id: 'general', label: 'General', icon: '⚙️' },
    { id: 'security', label: 'Security', icon: '🔒' },
    { id: 'email', label: 'AWS SES', icon: '📧' },
    { id: 'quiz', label: 'Quiz Defaults', icon: '📝' },
    { id: 'rate_limits', label: 'Rate Limits', icon: '⏱️' },
    { id: 'performance', label: 'Performance', icon: '⚡' }
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await API.get('/settings');
      setSettings(response.data);
      setSavedSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
      setMessage({ type: 'error', text: getErrorMessage(error, 'Failed to load settings') });
    } finally {
      setLoading(false);
    }
  };

  const handleSeedSettings = async () => {
    if (!window.confirm('This will create default settings. Existing settings will not be modified. Continue?')) {
      return;
    }

    try {
      setSeeding(true);
      const response = await API.post('/settings/seed');
      setMessage({ type: 'success', text: response.data.message });
      loadSettings();
    } catch (error) {
      console.error('Error seeding settings:', error);
      setMessage({ type: 'error', text: getErrorMessage(error, 'Failed to seed settings') });
    } finally {
      setSeeding(false);
    }
  };

  const handleReloadRateLimits = async () => {
    try {
      setReloadingRateLimits(true);
      setMessage(null);
      const response = await API.post('/settings/reload-rate-limits');
      setMessage({
        type: 'success',
        text: `Rate limits applied: ${response.data.api_rate_limit}, ${response.data.quiz_submission_rate_limit}`
      });
    } catch (error) {
      console.error('Error reloading rate limits:', error);
      setMessage({
        type: 'error',
        text: getErrorMessage(error, 'Failed to reload rate limits')
      });
    } finally {
      setReloadingRateLimits(false);
    }
  };

  const handleInputChange = (category, key, value, dataType) => {
    setSettings(prev => ({
      ...prev,
      [category]: prev[category]?.map(setting =>
        setting.setting_key === key
          ? { ...setting, setting_value: value }
          : setting
      )
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setMessage(null);

      // Prepare settings array
      const settingsArray = [];
      Object.entries(settings).forEach(([category, categorySettings]) => {
        categorySettings.forEach(setting => {
          settingsArray.push({
            category,
            setting_key: setting.setting_key,
            setting_value: setting.setting_value
          });
        });
      });

      const response = await API.put('/settings', { settings: settingsArray });

      setMessage({ type: 'success', text: response.data.message });
      loadSettings(); // Reload to get updated timestamps
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: getErrorMessage(error, 'Failed to save settings') });
    } finally {
      setSaving(false);
    }
  };

  const renderSettingInput = (category, setting) => {
    const { setting_key, setting_value, data_type, description } = setting;

    switch (data_type) {
      case 'boolean':
        return (
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <Label htmlFor={setting_key} className="font-medium">
                {setting_key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </Label>
              {description && (
                <p className="text-sm text-gray-500 mt-1">{description}</p>
              )}
            </div>
            <Switch
              id={setting_key}
              checked={setting_value === 'true'}
              onCheckedChange={(checked) =>
                handleInputChange(category, setting_key, checked ? 'true' : 'false', data_type)
              }
            />
          </div>
        );

      case 'integer':
        return (
          <div className="space-y-2">
            <Label htmlFor={setting_key} className="font-medium">
              {setting_key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Label>
            {description && (
              <p className="text-sm text-gray-500">{description}</p>
            )}
            <Input
              id={setting_key}
              type="number"
              value={setting_value}
              onChange={(e) => handleInputChange(category, setting_key, e.target.value, data_type)}
            />
          </div>
        );

      case 'json':
        return (
          <div className="space-y-2">
            <Label htmlFor={setting_key} className="font-medium">
              {setting_key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Label>
            {description && (
              <p className="text-sm text-gray-500">{description}</p>
            )}
            <textarea
              id={setting_key}
              className="w-full min-h-[100px] p-2 border rounded-md font-mono text-sm"
              value={setting_value}
              onChange={(e) => handleInputChange(category, setting_key, e.target.value, data_type)}
            />
          </div>
        );

      default: // string
        return (
          <div className="space-y-2">
            <Label htmlFor={setting_key} className="font-medium">
              {setting_key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Label>
            {description && (
              <p className="text-sm text-gray-500">{description}</p>
            )}
            <Input
              id={setting_key}
              type="text"
              value={setting_value}
              onChange={(e) => handleInputChange(category, setting_key, e.target.value, data_type)}
            />
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const hasSettings = Object.keys(settings).length > 0;
  const hasUnsavedChanges = hasSettings && JSON.stringify(settings) !== JSON.stringify(savedSettings);

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">System Settings</h1>
            <p className="text-gray-600 mt-1">Configure application-wide settings</p>
          </div>
          <div className="flex gap-2">
            {!hasSettings && (
              <Button
                onClick={handleSeedSettings}
                disabled={seeding}
                variant="outline"
              >
                {seeding ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Seeding...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Seed Default Settings
                  </>
                )}
              </Button>
            )}
            <Button onClick={loadSettings} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Reload UI
            </Button>
            <Button
              onClick={handleReloadRateLimits}
              variant="outline"
              disabled={reloadingRateLimits || !hasSettings}
              title="Apply rate limit changes to running server without restart"
            >
              {reloadingRateLimits ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  ⚡
                  <span className="ml-2">Apply Rate Limits</span>
                </>
              )}
            </Button>
            <Button
              onClick={handleSaveSettings}
              disabled={saving || !hasSettings}
              className={hasUnsavedChanges && !saving ? 'relative animate-pulse bg-indigo-600 hover:bg-indigo-700' : ''}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Unsaved changes banner */}
        {hasUnsavedChanges && !saving && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm font-medium">
            <AlertCircle className="w-4 h-4 flex-shrink-0 text-amber-500" />
            You have unsaved changes — click <strong className="mx-1">Save Changes</strong> to apply them.
          </div>
        )}

        {/* Messages */}
        {message && (
          <Alert className={message.type === 'success' ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}>
            {message.type === 'success' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
            <AlertDescription className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        {/* No Settings State */}
        {!hasSettings && (
          <Card>
            <CardContent className="py-12">
              <div className="text-center space-y-4">
                <div className="text-6xl">⚙️</div>
                <h3 className="text-xl font-semibold">No Settings Found</h3>
                <p className="text-gray-600">
                  Click "Seed Default Settings" to initialize the system settings.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Settings Tabs */}
        {hasSettings && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-6 w-full">
              {categories.map(cat => (
                <TabsTrigger key={cat.id} value={cat.id} className="flex items-center gap-2">
                  <span>{cat.icon}</span>
                  <span className="hidden md:inline">{cat.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {categories.map(cat => {
              const categorySettings = settings[cat.id] || [];

              return (
                <TabsContent key={cat.id} value={cat.id}>
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <span>{cat.icon}</span>
                        {cat.label} Settings
                      </CardTitle>
                      <CardDescription>
                        Configure {cat.label.toLowerCase()} settings for the application
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {categorySettings.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">
                          No settings in this category yet
                        </p>
                      ) : (
                        categorySettings.map(setting => (
                          <div key={setting.id}>
                            {renderSettingInput(cat.id, setting)}
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              );
            })}
          </Tabs>
        )}
      </div>
  );
}
